import { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";

import { exchangeSession } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * AuthCallback handles the URL fragment `#session_id=...` returned by
 * Emergent OAuth. It runs FIRST (before any other route) — see App.js.
 */
export default function AuthCallback() {
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = /session_id=([^&]+)/.exec(hash);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    // Wipe the fragment immediately so reloads don't re-process.
    if (window.history.replaceState) {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search
      );
    }

    if (!sessionId) {
      setError("No session id in URL");
      setTimeout(() => {
        window.location.replace("/");
      }, 1200);
      return;
    }

    (async () => {
      try {
        const user = await exchangeSession(sessionId);
        setUser(user);
        // Hard nav so the new session cookie is visible to every request.
        window.location.replace("/");
      } catch (e) {
        const msg =
          e?.response?.data?.detail || e?.message || "Sign-in failed";
        setError(msg);
        setTimeout(() => {
          window.location.replace("/");
        }, 2000);
      }
    })();
  }, [setUser]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      data-testid="auth-callback"
    >
      <div className="hk-card-strong px-8 py-7 text-center max-w-sm w-full">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Sparkles size={16} color="#00BFA5" />
          <span
            className="font-display"
            style={{
              color: "#00BFA5",
              fontWeight: 900,
              letterSpacing: "0.02em",
              fontSize: 18,
            }}
          >
            HIKARI STUDY
          </span>
        </div>
        {error ? (
          <div style={{ color: "#E84040", fontSize: 14 }} data-testid="auth-error">
            {error}
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <Loader2 size={18} className="animate-spin" color="#00BFA5" />
            <span style={{ color: "#E8F0F7", fontSize: 15 }}>
              Signing you in…
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
